import { z } from "zod";
import { LocalizedTextSchema } from "./common.js";
import { EntityTypeDefMetaSchema, RenderHintsSchema, UiHintsSchema } from "./entity.js";
import { ModuleIdSchema, NamespacedNameSchema } from "./ids.js";
import { PermissionDefSchema } from "./permissions.js";
import { CostHintSchema, SideEffectSchema } from "./tool.js";
const SEMVER_RE = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;
const isFunction = (v) => typeof v === "function";
const isZodSchema = (v) => v instanceof z.ZodType;
/**
 * Structural validation of a manifest (the first step of the module test pipeline).
 * Name-prefix rules: every entity type, tool, setting and permission is namespaced by the module id.
 */
export const ModuleManifestSchema = z
    .object({
    id: ModuleIdSchema,
    version: z.string().regex(SEMVER_RE, "must be semver"),
    scope: z.enum(["company", "world"]),
    dependsOn: z.record(ModuleIdSchema, z.string().min(1)),
    entityTypes: z.array(EntityTypeDefMetaSchema.extend({ props: z.custom(isZodSchema, "must be a Zod schema") })),
    tools: z.array(z.object({
        name: NamespacedNameSchema,
        description: z.string().min(1),
        input: z.custom(isZodSchema, "must be a Zod schema"),
        output: z.custom(isZodSchema, "must be a Zod schema"),
        sideEffect: SideEffectSchema,
        requiredPermissions: z.array(NamespacedNameSchema),
        costHint: CostHintSchema.optional(),
        handler: z.custom(isFunction, "must be a function"),
    })),
    settings: z.array(z.object({
        key: NamespacedNameSchema,
        scope: z.enum(["world", "company", "user"]),
        schema: z.custom(isZodSchema, "must be a Zod schema"),
        default: z.unknown(),
        level: z.enum(["basic", "advanced"]),
        label: LocalizedTextSchema,
        description: LocalizedTextSchema.optional(),
    })),
    permissions: z.array(PermissionDefSchema),
    ui: UiHintsSchema.optional(),
    render: RenderHintsSchema.optional(),
    migrations: z.array(z.object({
        id: z.string().min(1),
        description: z.string(),
        up: z.custom(isFunction, "must be a function"),
        down: z.custom(isFunction, "must be a function"),
    })),
    healthChecks: z
        .array(z.object({ name: z.string().min(1), run: z.custom(isFunction) }))
        .optional(),
})
    .superRefine((m, ctx) => {
    const prefix = `${m.id}.`;
    const check = (names, path) => {
        const seen = new Set();
        names.forEach((name, i) => {
            if (!name.startsWith(prefix)) {
                ctx.addIssue({
                    code: "custom",
                    path: [path, i],
                    message: `'${name}' must start with '${prefix}'`,
                });
            }
            if (seen.has(name)) {
                ctx.addIssue({ code: "custom", path: [path, i], message: `duplicate '${name}'` });
            }
            seen.add(name);
        });
    };
    check(m.entityTypes.map((t) => t.type), "entityTypes");
    check(m.tools.map((t) => t.name), "tools");
    check(m.settings.map((s) => s.key), "settings");
    check(m.permissions.map((p) => p.name), "permissions");
    const migrationIds = m.migrations.map((mig) => mig.id);
    if (new Set(migrationIds).size !== migrationIds.length) {
        ctx.addIssue({ code: "custom", path: ["migrations"], message: "duplicate migration id" });
    }
});
//# sourceMappingURL=module.js.map